/**
 * Tenant Service
 *
 * Handles tenant selection API calls for the multi-tenancy flow.
 * After login, if a user has multiple tenants, this service calls
 * POST /auth/select-tenant to re-issue the JWT with the chosen tenantId.
 *
 * Requirements: 3.4, 4.5
 */

import { TENANT_API_URL } from '../config/api.config';
import type { SelectTenantResponse } from '../types/tenant.types';
import { tokenService } from './token.service';

/**
 * Tenant Service Class
 */
export class TenantService {
  /**
   * Select a tenant for the current session.
   *
   * Calls POST /auth/select-tenant with the chosen tenantId.
   * On success the backend re-issues a JWT scoped to that tenant.
   */
  async selectTenant(tenantId: string): Promise<SelectTenantResponse> {
    try {
      const accessToken = tokenService.getAccessToken();
      if (!accessToken) {
        return { success: false, error: 'No access token available' };
      }

      // Include the refresh token so the backend can re-issue tokens
      // with the selected tenant's custom:tenantId claim via Cognito
      // REFRESH_TOKEN_AUTH + pre-token-gen clientMetadata.
      const refreshToken = tokenService.getRefreshToken();

      const response = await fetch(`${TENANT_API_URL}/auth/select-tenant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ tenantId, ...(refreshToken ? { refreshToken } : {}) }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message =
          errorData?.error?.message ||
          errorData?.message ||
          (typeof errorData?.error === 'string' ? errorData.error : null) ||
          `Tenant selection failed: ${response.status}`;
        return { success: false, error: message };
      }

      const data = await response.json();

      // Store new tokens issued for the selected tenant
      if (data.accessToken) {
        tokenService.storeTokens({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken || '',
          idToken: '',
          tokenType: 'Bearer',
          expiresIn: data.expiresIn || 900,
        });
      }

      return {
        success: true,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresIn: data.expiresIn,
        tenantId: data.tenantId || tenantId,
        roles: data.roles,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Tenant selection failed',
      };
    }
  }
}

// Export singleton instance
export const tenantService = new TenantService();
